import { useLingui } from '@lingui/react/macro';
import { UserPlus } from 'lucide-react';
import { useRef, useState } from 'react';
import { getPersonForBilling, PersonQuickCreate, personSource, type PersonQuickCreateResult } from '@/modules/person/public';
import { getWalletByCode, walletSource } from '@/modules/wallet/public';
import { lookupBankSlipZipCode } from '@/modules/bank-slip/public';
import { EntityPicker } from '@/shared/form/entity-picker';
import { formatDocument } from '@/shared/format/document';
import { Input } from '@/shared/ui/input';
import { MoneyField } from '@/shared/ui/money-field';
import { MaskedField } from '@/shared/ui/masked-field';
import { Button } from '@/shared/ui/button';
import { billingPayer, type BillingCreateValues } from './billing-workflow.model';
export function BillingCreateFields({
  values,
  onChange,
  onBusy,
}: {
  values: BillingCreateValues;
  onChange: (v: BillingCreateValues) => void;
  onBusy: (busy: boolean) => void;
}) {
  const { t } = useLingui();
  const request = useRef(0);
  const [failed, setFailed] = useState(false);
  const [addingPayer, setAddingPayer] = useState(false);
  const payer = values.payer;
  const updatePayer = (key: string, value: string) =>
    onChange({ ...values, payer: { ...payer, [key]: value } });
  // Pagador escolhido na lista ou recém-cadastrado: o legado lê o cadastro e preenche os dados do pagador
  // (`useGetNaturalOrLegalPerson`, NewChargesForm.tsx:65-108).
  const selectPerson = (
    value: string | null,
    label: string | null,
    discriminator: NonNullable<BillingCreateValues['personDiscriminator']>,
  ) => {
    const ticket = ++request.current;
    const next: BillingCreateValues = {
      ...values,
      personId: value ?? '',
      personIdDisplay: label,
      personDiscriminator: discriminator,
      payer: null,
    };
    onChange(next);
    setFailed(false);
    if (!value) {
      onBusy(false);
      return;
    }
    onBusy(true);
    void getPersonForBilling({ id: value, discriminator })
      .then((person) => {
        if (request.current === ticket)
          onChange({ ...next, payer: billingPayer(person, discriminator) });
      })
      .catch(() => {
        if (request.current === ticket) setFailed(true);
      })
      .finally(() => {
        if (request.current === ticket) onBusy(false);
      });
  };
  // "Adicionar pagador" (`handleAddPayer`, NewChargesForm.tsx:38-42): limpa o pagador e abre o cadastro.
  const addPayer = () => {
    ++request.current;
    onBusy(false);
    setFailed(false);
    onChange({ ...values, personId: '', personIdDisplay: null, payer: null });
    setAddingPayer(true);
  };
  // `onSuccessWhenAddPerson` (NewChargesStep.tsx:31-35): a pessoa criada vira o pagador, "nome - documento".
  const payerCreated = (person: PersonQuickCreateResult) => {
    const document = formatDocument(person.registrationNumber) ?? person.registrationNumber;
    selectPerson(person.id, `${person.name} - ${document}`, person.discriminator);
  };
  return (
    <div className="grid gap-4 @min-[45rem]/dialog:grid-cols-2 [&>:not(label)]:col-span-full">
      <label className="grid gap-1">
        {t`Carteira`}
        <EntityPicker
          source={walletSource()}
          value={values.walletCode}
          selectedLabel={values.walletCodeDisplay}
          onChange={(value, option) => {
            onChange({
              ...values,
              walletCode: value ?? '',
              walletCodeDisplay: option?.label ?? null,
              walletSelected: undefined,
            });
            setFailed(false);
            onBusy(true);
            void getWalletByCode(Number(value))
              .then((wallet) =>
                onChange({
                  ...values,
                  walletCode: value ?? '',
                  walletCodeDisplay: option?.label ?? null,
                  walletSelected: wallet,
                }),
              )
              .catch(() => setFailed(true))
              .finally(() => onBusy(false));
          }}
        />
      </label>
      <label className="grid gap-1">
        {t`Pagador`}
        <EntityPicker
          source={personSource}
          value={values.personId}
          selectedLabel={values.personIdDisplay}
          onChange={(value, option, item) =>
            selectPerson(
              value,
              option?.label ?? null,
              item?.discriminator === 'LegalPerson' ? 'LegalPerson' : 'NaturalPerson',
            )
          }
        />
      </label>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" onClick={addPayer}>
          <UserPlus aria-hidden />
          {t`Adicionar pagador`}
        </Button>
      </div>
      <PersonQuickCreate open={addingPayer} onOpenChange={setAddingPayer} onCreated={payerCreated} flow="payer" />
      {failed && (
        <p role="alert">{t`Não foi possível consultar o cadastro. Selecione novamente para tentar.`}</p>
      )}
      {payer && (
        <fieldset className="grid gap-3">
          <legend>{t`Dados do pagador`}</legend>
          <label>
            {t`Email`}
            <Input
              required
              type="email"
              value={payer.email ?? ''}
              onChange={(e) => updatePayer('email', e.target.value)}
            />
          </label>
          <label>
            {t`CEP`}
            <MaskedField
              required
              kind="cep"
              value={payer.zipCode}
              onChange={(zipCode) => {
                updatePayer('zipCode', zipCode);
                if (zipCode.length !== 8) return;
                setFailed(false);
                onBusy(true);
                void lookupBankSlipZipCode(zipCode)
                  .then((result) =>
                    onChange({
                      ...values,
                      payer: { ...payer, zipCode, addressName: result.address },
                    }),
                  )
                  .catch(() => setFailed(true))
                  .finally(() => onBusy(false));
              }}
            />
          </label>
          {(
            [
              ['addressName', t`Endereço`],
              ['addressNumber', t`Número`],
              ['addressComplement', t`Complemento`],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              {label}
              <Input
                required={key !== 'addressComplement'}
                value={typeof payer[key] === 'string' ? payer[key] : ''}
                onChange={(e) => updatePayer(key, e.target.value)}
              />
            </label>
          ))}
        </fieldset>
      )}
      <label>
        {t`Número do documento`}
        <Input
          required
          maxLength={10}
          value={values.documentNumber}
          onChange={(e) => onChange({ ...values, documentNumber: e.target.value })}
        />
      </label>
      <label>
        {t`Descrição`}
        <Input
          value={values.description ?? ''}
          onChange={(e) => onChange({ ...values, description: e.target.value })}
        />
      </label>
      <label>
        {t`Data de vencimento`}
        <Input
          required
          type="date"
          value={values.dueDate}
          onChange={(e) => onChange({ ...values, dueDate: e.target.value })}
        />
      </label>
      <label>
        {t`Data limite de pagamento`}
        <Input
          type="date"
          value={values.paymentLimitDate ?? ''}
          onChange={(e) => onChange({ ...values, paymentLimitDate: e.target.value || null })}
        />
      </label>
      <label>
        {t`Valor da cobrança`}
        <MoneyField
          required
          value={values.amount}
          onChange={(amount) => onChange({ ...values, amount })}
        />
      </label>
    </div>
  );
}
